/* Product page behaviour: gallery, build-time estimate and Downpay option labels. */

if (!customElements.get('pdp-gallery')) {
  customElements.define(
    'pdp-gallery',
    class PdpGallery extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('.pdp-gallery__main');
        this.mainImg = this.querySelector('.pdp-gallery__main-img');
        this.thumbs = [...this.querySelectorAll('.pdp-gallery__thumb')];

        this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.select(thumb)));
        if (this.main && this.hasAttribute('data-lightbox')) {
          this.main.addEventListener('click', () => this.openLightbox(this.main));
        }
        this.querySelector('[data-open-lightbox]')?.addEventListener('click', (event) =>
          this.openLightbox(event.currentTarget)
        );
      }

      select(thumb) {
        this.show(thumb.dataset, thumb);
      }

      // Called by <variant-selects> on variant change: selects the variant's thumbnail, or shows its
      // image in the main slot when it is past the visible thumbnails.
      showMedia(media) {
        const thumb = this.thumbs.find((item) => item.dataset.mediaId === String(media.id));
        if (thumb) return this.select(thumb);

        const src = media.preview_image?.src;
        if (!src) return;
        const sized = (width) => {
          const url = new URL(src, window.location.href);
          url.searchParams.set('width', width);
          return url.href;
        };
        this.show({
          src: sized(1100),
          srcset: [550, 720, 900, 1100, 1400].map((width) => `${sized(width)} ${width}w`).join(', '),
          alt: media.alt,
          mediaId: media.id,
        });
      }

      show({ src, srcset, alt, mediaId }, activeThumb) {
        if (!this.mainImg) return;
        this.mainImg.src = src;
        this.mainImg.srcset = srcset;
        this.mainImg.alt = alt || '';
        this.main.dataset.mediaId = mediaId;
        this.thumbs.forEach((item) =>
          item === activeThumb ? item.setAttribute('aria-current', 'true') : item.removeAttribute('aria-current')
        );
      }

      openLightbox(opener) {
        document.querySelector(this.dataset.modal)?.show(opener);
      }
    }
  );
}

/*
  Custom dropdown for options without a drawer (Size, Leaf…). The native <select> stays in the
  DOM, hidden, so <variant-selects> keeps reading it; choosing an item sets it and fires `change`.
*/
if (!customElements.get('pdp-select')) {
  customElements.define(
    'pdp-select',
    class PdpSelect extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('select');
        this.trigger = this.querySelector('.pdp-option__trigger');
        this.list = this.querySelector('.pdp-option__list');
        this.value = this.querySelector('[data-pdp-select-value]');
        this.onOutsideClick = (event) => {
          if (!this.contains(event.target)) this.close();
        };

        this.trigger.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
        this.trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          this.open();
        });
        this.list.addEventListener('keydown', this.onListKeydown.bind(this));
        this.list.addEventListener('click', (event) => {
          const item = event.target.closest('[role="option"]');
          if (item) this.choose(item.dataset.value);
        });
        this.select.addEventListener('change', () => {
          this.value.textContent = this.select.selectedOptions[0]?.textContent.trim() || '';
        });
      }

      get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
      }

      get items() {
        return [...this.list.children];
      }

      open() {
        document.querySelectorAll('pdp-select').forEach((other) => other !== this && other.close());
        this.list.replaceChildren(
          ...[...this.select.options].map((option, index) => {
            const item = document.createElement('li');
            item.id = `${this.list.id}-${index}`;
            item.className = 'pdp-option__item';
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', String(option.selected));
            item.dataset.value = option.value;
            item.textContent = option.textContent.trim();
            return item;
          })
        );
        this.list.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.setActive(this.select.selectedIndex);
        this.list.focus({ preventScroll: true });
        document.addEventListener('click', this.onOutsideClick);
      }

      close(returnFocus = false) {
        if (!this.isOpen) return;
        this.list.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.list.removeAttribute('aria-activedescendant');
        document.removeEventListener('click', this.onOutsideClick);
        if (returnFocus) this.trigger.focus();
      }

      setActive(index) {
        const items = this.items;
        if (!items.length) return;
        this.activeIndex = Math.max(0, Math.min(index, items.length - 1));
        items.forEach((item, i) => item.classList.toggle('is-active', i === this.activeIndex));
        this.list.setAttribute('aria-activedescendant', items[this.activeIndex].id);
        items[this.activeIndex].scrollIntoView({ block: 'nearest' });
      }

      choose(value) {
        this.close(true);
        if (this.select.value === value) return;
        this.select.value = value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onListKeydown(event) {
        const keys = {
          ArrowDown: () => this.setActive(this.activeIndex + 1),
          ArrowUp: () => this.setActive(this.activeIndex - 1),
          Home: () => this.setActive(0),
          End: () => this.setActive(this.items.length - 1),
          Enter: () => this.choose(this.items[this.activeIndex].dataset.value),
          ' ': () => this.choose(this.items[this.activeIndex].dataset.value),
          Escape: () => this.close(true),
        };
        if (event.key === 'Tab') return this.close();
        if (!keys[event.key]) return;
        event.preventDefault();
        keys[event.key]();
      }
    }
  );
}

(function () {
  const root = document.querySelector('.pdp');
  if (!root) return;

  /* ---------- Build time + delivery estimate ---------- */

  function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  function nextWeekday(date) {
    while (date.getDay() === 0 || date.getDay() === 6) date = addDays(date, 1);
    return date;
  }

  function renderBuildTime() {
    const source = document.querySelector('[data-pdp-build-time]');
    if (!source) return;

    let data;
    try {
      data = JSON.parse(source.textContent);
    } catch (error) {
      return;
    }

    const label = String(data.label)
      .replace(/(\d)\s*-\s*(\d)/g, '$1–$2')
      .toLowerCase();
    root.querySelectorAll('[data-build-time-value]').forEach((el) => (el.textContent = label));
    root.querySelectorAll('[data-build-time-row]').forEach((el) => (el.hidden = false));

    const card = root.querySelector('[data-build-time-card]');
    if (!card) return;

    const delivery = nextWeekday(addDays(new Date(), data.maxDays || 0));
    const weekStart = addDays(delivery, -((delivery.getDay() + 6) % 7));
    const date = weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
    card.querySelector('[data-delivery-value]').textContent = `week of ${date}`;

    const longNote = card.querySelector('[data-build-long-note]');
    if (longNote) longNote.hidden = (data.minDays || 0) / 7 < Number(card.dataset.longWeeks);

    card.hidden = false;
  }

  /* ---------- Option drawers: after each choice, open the next option until the last one ---------- */

  function currentVariantId() {
    return root.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value;
  }

  function openWood() {
    const drawer = root.querySelector('.main-options-drawer');
    if (!drawer?.querySelector('.image-btn-wrapper')) return false;
    drawer.classList.add('showModal');
    return true;
  }

  function openStain() {
    const drawer = root.querySelector('.variant__description_drawer');
    const panel = drawer?.querySelector(`.sidebar-inner[data-variant-id="${currentVariantId()}"]`);
    if (!panel) return false;
    drawer.style.display = '';
    drawer.querySelectorAll('.sidebar-inner.active').forEach((el) => el.classList.remove('active'));
    panel.classList.add('active');
    return true;
  }

  function openSidebar(selector) {
    const drawer = root.querySelector(selector);
    if (!drawer || drawer.classList.contains('hidden') || !drawer.querySelector('.image-btn-wrapper')) return false;
    drawer.style.display = 'block';
    drawer.classList.add('active');
    return true;
  }

  // In the order the cards appear in the buy box.
  const optionSteps = [
    { card: '.changeSelect', open: openWood },
    { card: '.stain_custom_options', open: openStain },
    { card: '.hardware-data', open: () => openSidebar('.sidebar_hardware') },
    { card: '.fabric-data', open: () => openSidebar('.sidebar_fabric') },
  ];

  function openNextOption(fromIndex) {
    for (let i = fromIndex + 1; i < optionSteps.length; i++) {
      const card = root.querySelector(optionSteps[i].card);
      if (card && !card.classList.contains('hidden') && optionSteps[i].open()) return;
    }
  }

  // A new wood invalidates the stain picked for the previous one.
  function resetStain() {
    const select = root.querySelector('.stain_custom_options select');
    if (!select?.value) return;
    select.querySelectorAll('option').forEach((option) => {
      option.value = '';
      option.textContent = 'Select Wood';
    });
    select.value = '';
    root.querySelector('.stain_custom_options .variantValue-text').textContent = 'Select';
    root.querySelector('.stain_custom_options .variantValue-svg')?.classList.add('is-hidden');
  }

  // Run after the drawers' own handlers have saved the choice and closed themselves.
  root.addEventListener('change', (event) => {
    if (!event.target.matches('.main-options-drawer .radio-image-input')) return;
    resetStain();
    setTimeout(() => openNextOption(0));
  });
  root.addEventListener('click', (event) => {
    if (event.target.closest('.variant__description_drawer .choose_btn a')) setTimeout(() => openNextOption(1));
    else if (event.target.closest('.sidebar_hardware .hardware-option-btn a')) setTimeout(() => openNextOption(2));
  });

  /* ---------- Upholstery (Buckeye Rockers): separate fabric and leather menus ---------- */

  // The fabric drawer lists standard fabrics and "Genuine Leather …" swatches; show only the set
  // that matches the Upholstery option.
  const upholsterySelect = root.querySelector('select[name="options[Upholstery]"]');

  function filterFabricDrawer() {
    const leather = upholsterySelect.value === 'Genuine Leather';
    const isLeather = (name) => name.trim().startsWith('Genuine Leather');
    const word = leather ? 'Leather' : 'Fabric';

    root.querySelectorAll('.sidebar_fabric .image-btn-wrapper').forEach((item) => {
      const name = item.querySelector('.choose_btn a')?.dataset.value || '';
      item.style.display = isLeather(name) === leather ? '' : 'none';
    });
    const heading = root.querySelector('.sidebar_fabric .heading-option');
    if (heading) heading.textContent = `${word} Options`;
    const label = root.querySelector('.fabric-data .pdp-option__label');
    if (label) label.textContent = `Select ${word} Option`;

    // Clear a choice made under the other upholstery.
    const chosen = root.querySelector('.fabric_options select option');
    if (!chosen?.value || isLeather(chosen.value) === leather) return;
    chosen.value = '';
    chosen.textContent = 'Select';
    root.querySelector('.fabric-variantValue .variantValue-text').textContent = 'Select';
    root.querySelector('.fabric-variantValue .variantValue-svg')?.classList.add('is-hidden');
  }

  if (upholsterySelect) {
    filterFabricDrawer();
    upholsterySelect.addEventListener('change', filterFabricDrawer);
  }

  /* ---------- Downpay: amounts + descriptions on the deposit / pay-in-full options ---------- */

  const quantityInput = root.querySelector('.quantity__input');

  function formatMoney(cents) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(
      cents / 100
    );
  }

  function setLabel(label, title, description) {
    if (!label) return;
    if (!label.dataset.pdpOriginal) label.dataset.pdpOriginal = label.textContent.trim();
    label.innerHTML = '';
    const titleEl = document.createElement('span');
    titleEl.className = 'pdp-pay__title';
    titleEl.textContent = title;
    label.appendChild(titleEl);
    if (description) {
      const descEl = document.createElement('span');
      descEl.className = 'pdp-pay__desc';
      descEl.textContent = description;
      label.appendChild(descEl);
    }
  }

  function decoratePayment() {
    const wrapper = root.querySelector('#downpay-product-wrapper');
    const depositLabel = wrapper?.querySelector('.deposit-option-label');
    const fullLabel = wrapper?.querySelector('.pay-in-full-label');
    if (!depositLabel || !fullLabel) return;

    const original = depositLabel.dataset.pdpOriginal || depositLabel.textContent;
    const percent = parseFloat((original.match(/(\d+(?:\.\d+)?)\s*%/) || [])[1]);
    if (!percent) return;

    const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
    const price = Number(root.dataset.variantPrice) * quantity;

    observer.disconnect();
    setLabel(
      depositLabel,
      `${percent}% deposit — ${formatMoney(Math.round((price * percent) / 100))} today`,
      root.dataset.depositDescription
    );
    setLabel(fullLabel, `Pay in full — ${formatMoney(price)}`, root.dataset.fullDescription);
    wrapper.setAttribute('data-pdp-pay-ready', '');
    observer.observe(root, { childList: true, subtree: true });
  }

  // Downpay renders its options after load and may re-render them.
  const observer = new MutationObserver(() => {
    const label = root.querySelector('#downpay-product-wrapper .deposit-option-label');
    if (label && !label.querySelector('.pdp-pay__title')) decoratePayment();
  });

  renderBuildTime();
  decoratePayment();
  observer.observe(root, { childList: true, subtree: true });

  quantityInput?.addEventListener('change', decoratePayment);
  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data?.variant) root.dataset.variantPrice = event.data.variant.price;
      decoratePayment();
    });
  }
})();
